import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Tag,
  Avatar,
  Dropdown,
  Space,
  message,
  Spin
} from 'antd';
import {
  BookOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trainings, setTrainings] = useState([]);
  const [stats, setStats] = useState({
    total_assigned: 0,
    in_progress: 0,
    completed: 0,
    total_labs: 0,
    completed_labs: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [trainingsRes, statsRes] = await Promise.allSettled([
        api.get('/student/assignments'),
        api.get('/student/stats')
      ]);

      if (trainingsRes.status === 'fulfilled') {
        setTrainings(trainingsRes.value.data || []);
      }

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data || stats);
      }
    } catch (error) {
      message.error('Failed to load dashboard data');
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/student/dashboard'),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/student/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'blue',
      in_progress: 'orange',
      completed: 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      assigned: <ClockCircleOutlined />,
      in_progress: <BookOutlined />,
      completed: <CheckCircleOutlined />
    };
    return icons[status] || null;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/student/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Student Dashboard</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>My Learning Dashboard</Title>
          <Text type="secondary">Track your progress and continue your training</Text>
        </div>

        {/* Stats Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Text type="secondary">Assigned Trainings</Text>
                  <Title level={2} style={{ margin: 0 }}>{stats.total_assigned}</Title>
                </div>
                <Avatar size={48} style={{ backgroundColor: '#1890ff' }} icon={<BookOutlined />} />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Text type="secondary">In Progress</Text>
                  <Title level={2} style={{ margin: 0 }}>{stats.in_progress}</Title>
                </div>
                <Avatar size={48} style={{ backgroundColor: '#faad14' }} icon={<ClockCircleOutlined />} />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Text type="secondary">Completed</Text>
                  <Title level={2} style={{ margin: 0 }}>{stats.completed}</Title>
                </div>
                <Avatar size={48} style={{ backgroundColor: '#52c41a' }} icon={<CheckCircleOutlined />} />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Text type="secondary">Labs Progress</Text>
                  <Title level={2} style={{ margin: 0 }}>{stats.completed_labs}/{stats.total_labs}</Title>
                </div>
                <Avatar size={48} style={{ backgroundColor: '#722ed1' }} icon={<TrophyOutlined />} />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Trainings List */}
        <Card title="My Trainings" style={{ marginBottom: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : trainings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BookOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <Paragraph type="secondary">No trainings assigned yet</Paragraph>
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {trainings.map(training => (
                <Col xs={24} md={12} lg={8} key={training.id}>
                  <Card
                    hoverable
                    onClick={() => navigate(`/student/training/${training.id}`)}
                    style={{ height: '100%' }}
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Title level={4} style={{ margin: 0 }}>{training.name}</Title>
                        <Tag color={getStatusColor(training.status)} icon={getStatusIcon(training.status)}>
                          {training.status.toUpperCase().replace('_', ' ')}
                        </Tag>
                      </div>

                      <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 0 }}
                      >
                        {training.description}
                      </Paragraph>

                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Type: <Tag color="blue">{training.type || 'N/A'}</Tag>
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                          Labs: <strong>{training.completed_labs || 0}/{training.total_labs || 0}</strong>
                        </Text>
                      </div>

                      {training.total_labs > 0 && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>Progress</Text>
                          <Progress
                            percent={Math.round(((training.completed_labs || 0) / training.total_labs) * 100)}
                            size="small"
                            status={training.status === 'completed' ? 'success' : 'active'}
                          />
                        </div>
                      )}

                      {training.assigned_at && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Assigned: {new Date(training.assigned_at).toLocaleDateString()}
                        </Text>
                      )}
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card>
      </Content>
    </Layout>
  );
}
